import { Roles } from '~/data/user';

export type EmployeeRolesSelectionState = {
  EMPLOYEE: boolean;
  DEPUTY_MANAGER: boolean;
  IT_ADMIN: boolean;
  ALT_MANAGER: boolean;
  MANAGER: boolean;
  HR_DEV: boolean;
  HR_STAFF: boolean;
  HR_ADMIN: boolean;
  HR_ADMIN_LIMITED: boolean;
  HR_SALARY: boolean;
  HR_PEOP: boolean;
  HR_PEOP_DEV: boolean;
  RECRUITER: boolean;
};

export const convertRolesToEmployeeRolesSelectionState = (
  roles: (string | Roles)[],
): EmployeeRolesSelectionState => {
  const checkIfHasRole = (role: Roles) => roles.includes(role);

  return {
    EMPLOYEE: checkIfHasRole(Roles.EMPLOYEE),
    DEPUTY_MANAGER: checkIfHasRole(Roles.DEPUTY_MANAGER),
    IT_ADMIN: checkIfHasRole(Roles.IT_ADMIN),
    ALT_MANAGER: checkIfHasRole(Roles.ALT_MANAGER),
    MANAGER: checkIfHasRole(Roles.MANAGER),
    HR_DEV: checkIfHasRole(Roles.HR_DEV),
    HR_STAFF: checkIfHasRole(Roles.HR_STAFF),
    HR_ADMIN: checkIfHasRole(Roles.HR_ADMIN),
    HR_ADMIN_LIMITED: checkIfHasRole(Roles.HR_ADMIN_LIMITED),
    HR_SALARY: checkIfHasRole(Roles.HR_SALARY),
    HR_PEOP: checkIfHasRole(Roles.HR_PEOP),
    HR_PEOP_DEV: checkIfHasRole(Roles.HR_PEOP_DEV),
    RECRUITER: checkIfHasRole(Roles.RECRUITER),
  };
};

export const convertEmployeeRolesSelectionStateToRoles = (
  empRoles: EmployeeRolesSelectionState,
): Roles[] => {
  return Object.keys(empRoles).filter(
    (key) => empRoles[key as keyof EmployeeRolesSelectionState],
  ) as Roles[];
};

export const resolveNewEmployeeRolesSelectionState =
  (employeeRolesSetup: EmployeeRolesSelectionState) =>
  (role: string, isChecked: boolean): EmployeeRolesSelectionState => {
    const {
      IT_ADMIN,
      DEPUTY_MANAGER,
      MANAGER,
      ALT_MANAGER,
      HR_STAFF,
      HR_ADMIN,
      HR_ADMIN_LIMITED,
      HR_PEOP,
      HR_PEOP_DEV,
    } = employeeRolesSetup;
    const retainRoles_FullManagers: Partial<EmployeeRolesSelectionState> = {
      MANAGER,
      ALT_MANAGER,
    };
    const retainRoles_AllManagers: Partial<EmployeeRolesSelectionState> = {
      DEPUTY_MANAGER,
      MANAGER,
      ALT_MANAGER,
    };
    const retainRoles_HRs: Partial<EmployeeRolesSelectionState> = {
      HR_STAFF,
      HR_ADMIN_LIMITED,
      HR_ADMIN,
      HR_PEOP,
      HR_PEOP_DEV,
    };

    const disableOtherRoles = (
      enabledRoles: Partial<EmployeeRolesSelectionState>,
    ): EmployeeRolesSelectionState => {
      return {
        EMPLOYEE: false,
        DEPUTY_MANAGER: false,
        IT_ADMIN: false,
        ALT_MANAGER: false,
        MANAGER: false,
        HR_DEV: false,
        HR_STAFF: false,
        HR_ADMIN: false,
        HR_ADMIN_LIMITED: false,
        HR_SALARY: false,
        HR_PEOP: false,
        HR_PEOP_DEV: false,
        RECRUITER: false,
        ...enabledRoles,
      };
    };

    if (isChecked) {
      switch (role) {
        // Singular Roles
        case Roles.EMPLOYEE:
          return disableOtherRoles({
            EMPLOYEE: true,
          });
        case Roles.HR_DEV:
          return disableOtherRoles({
            HR_DEV: true,
          });
        case Roles.HR_SALARY:
          return disableOtherRoles({
            HR_SALARY: true,
          });
        case Roles.RECRUITER:
          return disableOtherRoles({
            RECRUITER: true,
          });

        // Manager Roles
        case Roles.DEPUTY_MANAGER:
          return disableOtherRoles({
            ...retainRoles_AllManagers,
            DEPUTY_MANAGER: true,
          });
        case Roles.ALT_MANAGER:
          return disableOtherRoles({
            ...retainRoles_AllManagers,
            ...retainRoles_HRs,
            ALT_MANAGER: true,
          });
        case Roles.MANAGER:
          return disableOtherRoles({
            IT_ADMIN,
            ...retainRoles_AllManagers,
            ...retainRoles_HRs,
            MANAGER: true,
          });

        // HR Roles
        case Roles.HR_STAFF:
          return disableOtherRoles({
            ...retainRoles_FullManagers,
            HR_STAFF: true,
          });
        case Roles.HR_ADMIN:
          return disableOtherRoles({
            ...retainRoles_FullManagers,
            HR_ADMIN: true,
          });
        case Roles.HR_ADMIN_LIMITED:
          return disableOtherRoles({
            ...retainRoles_FullManagers,
            HR_ADMIN_LIMITED: true,
          });
        case Roles.HR_PEOP:
          return disableOtherRoles({
            ...retainRoles_FullManagers,
            HR_PEOP: true,
          });
        case Roles.HR_PEOP_DEV:
          return disableOtherRoles({
            ...retainRoles_FullManagers,
            HR_PEOP_DEV: true,
          });

        // Other
        case Roles.IT_ADMIN:
          return disableOtherRoles({
            MANAGER,
            IT_ADMIN: true,
          });
        default:
          return employeeRolesSetup;
      }
    }

    const resolveRoleDeselect = () => {
      const newEmpRoles = { ...employeeRolesSetup, [role]: false };

      return Object.values(newEmpRoles).every((roleEnabled) => !roleEnabled)
        ? { ...newEmpRoles, EMPLOYEE: true }
        : newEmpRoles;
    };

    return resolveRoleDeselect();
  };
